import { inject, Injectable } from '@angular/core';
import { AccessPermissionEntry } from 'src/app/models/api/accountManagement.model';
import { PermissionEnum } from 'src/app/models/auth0/permission.enum';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { LegacyRole, Permission, permissionConfig } from 'src/app/models/permissions';
import { FeatureFlagsService } from '../featureFlags/feature-flags.service';
import { UserService } from '../user/user.service';

export interface IntegrationWithId {
  id: string;
}

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private readonly _featureFlagsService = inject(FeatureFlagsService);

  constructor(private readonly _userService: UserService) {}

  /**
   * Unified permission check for actions.
   * If BE provides requiredPermissions, uses those. Otherwise uses userHasPermission with constructed permission.
   *
   * @param entity The entity (e.g., 'inventory', 'violation', 'step')
   * @param requiredRole The minimum required role (admin, operator, analyst, viewer)
   * @param integrationId Optional integration ID
   * @param requiredPermissions Optional BE-provided permissions array
   * @returns true if user has permission
   */
  public checkActionPermission(
    entity: string,
    requiredRole: string,
    integrationId: string | undefined,
    requiredPermissions: string[] | undefined
  ): boolean {
    if (requiredPermissions && requiredPermissions.length > 0 && integrationId) {
      return this.hasAnyRequiredPermission(requiredPermissions, integrationId);
    }
    // Construct permission string: entity_role (e.g., 'violation_operation')
    const normalizedRole = this._normalizeRole(requiredRole);
    const permissionKey =
      `${entity.toLowerCase()}_${normalizedRole === 'operator' ? 'operation' : normalizedRole}` as Permission;
    return this.userHasPermission(permissionKey, integrationId);
  }

  /**
   * Extracts role string from PermissionEnum.
   * Example: PermissionEnum.operator ('user:operator') => 'operator'
   */
  public extractRoleFromPermissionEnum(permission: PermissionEnum): string {
    const permStr = permission.toString().split(':')[1];
    return this._normalizeRole(permStr);
  }

  private _normalizeRole(role: string): string {
    // Handle 'operation' and 'operator' as equivalent -> 'operator'
    if (role === 'operation') {
      return 'operator';
    }
    return role;
  }

  public hasOnlyViewerPermissions(): boolean {
    if (this._isAdmin()) {
      return false;
    }

    // Check RBAC accessPermissions for any non-viewer permission
    const accessPermissions = this._userService.accessPermissions;
    if (Object.keys(accessPermissions).length > 0) {
      const hasHigherPermission = Object.keys(accessPermissions).some((key) => !key.endsWith('_viewer'));
      if (hasHigherPermission) {
        return false;
      }
    }

    // Legacy check: user has viewer but no higher roles
    return (
      this._userService.userPermissions.includes(PermissionEnum.viewer.toString()) &&
      !this._userService.userPermissions.includes(PermissionEnum.analyst.toString()) &&
      !this._userService.userPermissions.includes(PermissionEnum.operator.toString())
    );
  }

  /**
   * Check if the current user is an admin.
   * Admin check is based on JWT token only - RBAC/access groups don't apply to admins.
   * @returns true if the user has admin role
   */
  public isAdmin(): boolean {
    return this._isAdmin();
  }

  private _isAdmin(): boolean {
    return (
      this._userService.userPermissions.includes(PermissionEnum.admin.toString()) ||
      this._userService.apiUserRoles.some((role) => role.toLowerCase() === 'admin')
    );
  }

  private _hasIntegrationAccess(permValue: AccessPermissionEntry, integrationId?: string): boolean {
    if (permValue.hasAccessToAll || !integrationId) {
      return true;
    }

    // Check if the specific integration is in the allowed list
    return permValue.integrationIds.includes(integrationId);
  }

  /**
   * Check if the user has any of the required permissions for a specific integration.
   * Used for checking BE-provided requiredPermissions on actions.
   * @param requiredPermissions Array of permission keys from BE (e.g., ['inventory_operation'])
   * @param integrationId The integration ID to check access for
   * @returns true if user has at least one of the required permissions for the integration
   */
  public hasAnyRequiredPermission(requiredPermissions: string[], integrationId: string): boolean {
    if (this._isAdmin()) {
      return true;
    }

    if (!requiredPermissions || requiredPermissions.length === 0) {
      return true;
    }

    // If FF is off, use legacy permission check
    if (!this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.rbacAccessManagement)) {
      return this._hasLegacyPermission(requiredPermissions);
    }

    const accessPermissions = this._userService.accessPermissions;

    // If no accessPermissions structure, fall back to legacy permission check
    if (Object.keys(accessPermissions).length === 0) {
      return this._hasLegacyPermission(requiredPermissions);
    }

    for (const requiredPerm of requiredPermissions) {
      // Check exact permission and global fallback
      const level = requiredPerm.substring(requiredPerm.lastIndexOf('_'));
      const keysToCheck = [requiredPerm, `global${level}`];

      for (const key of keysToCheck) {
        const permValue = accessPermissions[key];
        if (permValue && this._hasIntegrationAccess(permValue, integrationId)) {
          return true;
        }
      }
    }

    return false;
  }

  private _hasLegacyPermission(requiredPermissions: string[]): boolean {
    for (const requiredPerm of requiredPermissions) {
      const [, level] = requiredPerm.split('_');
      const permissionEnum = `zenity:${level}`;
      if (this._userService.userPermissions.includes(permissionEnum)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check if user has a specific permission.
   *
   * NEW RBAC API:
   * - If integrationId is provided: checks permission for that specific integration
   * - If integrationId is NOT provided: requires GLOBAL permission (hasAccessToAll)
   *
   * @param permission - Permission from Permission type
   * @param integrationId - Optional integration ID for scoped check
   * @returns true if user has the required permission
   */
  public userHasPermission(permission: Permission, integrationId?: string): boolean {
    // Admin always has all permissions
    if (this._isAdmin()) {
      return true;
    }

    const accessPermissions = this._userService.accessPermissions;

    if (Object.keys(accessPermissions).length === 0) {
      return this._checkLegacyRolePermission(permission);
    }

    return this._checkAccessPermission(permission, integrationId);
  }

  /**
   * Legacy fallback: check permission using JWT token roles and permissionConfig mapping.
   */
  private _checkLegacyRolePermission(permission: Permission): boolean {
    const allowedRoles = permissionConfig[permission];
    if (!allowedRoles || allowedRoles.length === 0) {
      return false;
    }

    // Get user's legacy roles from JWT
    const userLegacyRoles = this._getUserLegacyRoles();

    // Check if user has any of the allowed roles
    return userLegacyRoles.some((role) => allowedRoles.includes(role));
  }

  /**
   * New RBAC check using accessPermissions from API.
   */
  private _checkAccessPermission(permission: Permission, integrationId?: string): boolean {
    const accessPermissions = this._userService.accessPermissions;
    const level = permission.substring(permission.lastIndexOf('_'));
    const keysToCheck = [permission, `global${level}`];

    for (const key of keysToCheck) {
      const permValue = accessPermissions[key];
      if (permValue && this._hasIntegrationAccess(permValue, integrationId)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if user has ANY operation-level permission.
   * Used for pages like Create Playbook where permission is checked after integration selection.
   */
  public hasAnyOperationPermission(): boolean {
    if (this._isAdmin()) {
      return true;
    }

    const accessPermissions = this._userService.accessPermissions;
    if (Object.keys(accessPermissions).length === 0) {
      // Legacy check: user has operator role
      return this._userService.userPermissions.some((p) => p === 'user:operator' || p === 'user:admin');
    }

    // RBAC check: user has any operation-level permission
    return Object.keys(accessPermissions).some((key) => key.endsWith('_operation'));
  }

  /**
   * Extract legacy roles from JWT token permissions.
   */
  private _getUserLegacyRoles(): LegacyRole[] {
    const roles: LegacyRole[] = [];
    const permissions = this._userService.userPermissions;

    for (const perm of permissions) {
      if (perm.startsWith('user:')) {
        const role = perm.split(':')[1] as LegacyRole;
        if (['admin', 'operator', 'analyst', 'viewer'].includes(role)) {
          roles.push(role);
        }
      }
    }

    return roles;
  }

  /**
   * Filter integrations based on user's access permissions for a specific permission type.
   * Returns only integrations the user has access to based on their accessPermissions.
   *
   * @param integrations Array of integrations to filter (must have an 'id' property)
   * @param permissionType The permission type to check (e.g., 'violation_viewer', 'inventory_viewer')
   * @returns Filtered array of integrations the user has access to
   */
  public filterIntegrationsByPermission<T extends IntegrationWithId>(integrations: T[], permissionType: string): T[] {
    // Admin has access to all integrations
    if (this._isAdmin()) {
      return integrations;
    }

    // If RBAC feature flag is disabled, return all integrations (legacy behavior)
    if (!this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.rbacAccessManagement)) {
      return integrations;
    }

    const accessPermissions = this._userService.accessPermissions;

    // If no accessPermissions structure, return all (legacy behavior)
    if (Object.keys(accessPermissions).length === 0) {
      return integrations;
    }

    const keysToCheck = this._getPermissionKeysToCheck(permissionType);

    if (this._hasAccessToAllIntegrations(accessPermissions, keysToCheck)) {
      return integrations;
    }

    const allowedIntegrationIds = this._collectAllowedIntegrationIds(accessPermissions, keysToCheck);

    // If no specific integrations are allowed, return empty array
    if (allowedIntegrationIds.size === 0) {
      return [];
    }

    // Filter integrations to only those the user has access to
    return integrations.filter((integration) => allowedIntegrationIds.has(integration.id));
  }

  /**
   * Get all integration IDs the user has access to for a specific permission type.
   * Useful when you need just the IDs without the full integration objects.
   *
   * @param permissionType The permission type to check (e.g., 'violation_viewer', 'inventory_viewer')
   * @returns Set of integration IDs the user has access to, or null if user has access to all
   */
  public getAllowedIntegrationIds(permissionType: string): Set<string> | null {
    // Admin has access to all integrations, or RBAC feature flag is disabled
    if (this._isAdmin() || !this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.rbacAccessManagement)) {
      return null;
    }

    const { accessPermissions } = this._userService;

    // If no accessPermissions structure, return null (access to all)
    if (!accessPermissions || Object.keys(accessPermissions).length === 0) {
      return null;
    }

    const keysToCheck = this._getPermissionKeysToCheck(permissionType);

    if (this._hasAccessToAllIntegrations(accessPermissions, keysToCheck)) {
      return null;
    }

    return this._collectAllowedIntegrationIds(accessPermissions, keysToCheck);
  }

  /**
   * Get the permission keys to check for a given permission type.
   * Includes both the specific permission and the global fallback.
   */
  private _getPermissionKeysToCheck(permissionType: string): string[] {
    const level = permissionType.substring(permissionType.lastIndexOf('_'));
    return [permissionType, `global${level}`];
  }

  /**
   * Check if user has access to all integrations via any of the relevant permission keys.
   */
  private _hasAccessToAllIntegrations(
    accessPermissions: Record<string, AccessPermissionEntry>,
    keysToCheck: string[]
  ): boolean {
    return keysToCheck.some((key) => accessPermissions[key]?.hasAccessToAll);
  }

  /**
   * Collect all allowed integration IDs from relevant permissions.
   */
  private _collectAllowedIntegrationIds(
    accessPermissions: Record<string, AccessPermissionEntry>,
    keysToCheck: string[]
  ): Set<string> {
    const allowedIntegrationIds = new Set<string>();
    for (const key of keysToCheck) {
      const permValue = accessPermissions[key];
      if (permValue?.integrationIds) {
        permValue.integrationIds.forEach((id) => allowedIntegrationIds.add(id));
      }
    }
    return allowedIntegrationIds;
  }
}
